import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { type PropertyMetaConfig, statsFunctions } from '@blocksuite/data-view';
import {
  computeFormulaValue,
  FORMULA_PROPERTY_TYPE,
  type FormulaPropertyData,
} from '@blocksuite/data-view/property-presets';
import type { EditorHost } from '@blocksuite/std';

import type {
  RelationPropertyData,
  RelationRawValue,
} from '../relation/types.js';
import { ROLLUP_PROPERTY_TYPE, type RollupPropertyData } from './types.js';

export type RollupComputationStatus =
  | 'not-configured'
  | 'empty'
  | 'loading'
  | 'missing'
  | 'cycle'
  | 'ok';

export interface RollupComputation {
  status: RollupComputationStatus;
  value: string;
}

interface ComputeRollupValueOptions {
  view: {
    cellGetOrCreate: (
      rowId: string,
      propertyId: string
    ) => {
      value$?: { value: unknown };
      jsonValue$?: { value: unknown };
    };
    propertyGetOrCreate: (propertyId: string) => {
      data$: { value: unknown };
    };
    manager: {
      dataSource: {
        propertyMetaGet: (type: string) => PropertyMetaConfig | undefined;
      };
    };
  };
  rowId: string;
  data: RollupPropertyData;
  std: EditorHost['std'] | undefined;
  onLoadNeeded?: (docId: string) => void;
}

interface RollupEntry {
  rawValue: unknown;
  jsonValue: unknown;
}

const empty = (status: RollupComputationStatus): RollupComputation => ({
  status,
  value: '',
});

const rawValueToString = (
  meta: PropertyMetaConfig,
  value: unknown,
  data: unknown
): string => {
  try {
    return (
      meta.config.rawValue.toString({
        value: value as never,
        data: data as never,
      }) ?? ''
    );
  } catch {
    return value == null ? '' : String(value);
  }
};

const getRawCellValue = (
  targetDb: DatabaseBlockModel,
  rowId: string,
  propertyId: string,
  meta: PropertyMetaConfig
): unknown => {
  if (propertyId === 'title') return rowId;
  // Read through cells$ so @preact computed() subscribers are invalidated when
  // target rows change. props.cells is not signal-backed.
  return (
    targetDb.props.cells$.value[rowId]?.[propertyId]?.value ??
    meta.config.rawValue.default()
  );
};

const buildFormulaView = (
  targetDb: DatabaseBlockModel,
  dataSource: ComputeRollupValueOptions['view']['manager']['dataSource']
) => {
  return {
    rows$: {
      value: targetDb.children.map(row => ({ rowId: row.id })),
    },
    propertyGetOrCreate(colId: string) {
      const column = targetDb.props.columns$.value.find(c => c.id === colId);
      return {
        type$: { value: column?.type ?? '' },
        data$: { value: column?.data ?? {} },
      };
    },
    cellGetOrCreate(rowId: string, colId: string) {
      const column = targetDb.props.columns$.value.find(c => c.id === colId);
      const meta = column ? dataSource.propertyMetaGet(column.type) : undefined;
      if (!column || !meta) {
        return { value$: { value: null }, jsonValue$: { value: null } };
      }
      const rawValue = getRawCellValue(targetDb, rowId, colId, meta);
      if (column.type === FORMULA_PROPERTY_TYPE) {
        // Formula refs recurse through computeFormulaValue; only expose the raw
        // per-row override here to avoid eagerly evaluating the same cell.
        return { value$: { value: rawValue }, jsonValue$: { value: null } };
      }
      return {
        value$: { value: rawValue },
        jsonValue$: {
          value: meta.config.rawValue.toJson({
            value: rawValue as never,
            data: column.data as never,
            dataSource: dataSource as never,
          }),
        },
      };
    },
  };
};

const collectTargetEntries = (
  targetDb: DatabaseBlockModel,
  targetPropertyId: string,
  refs: RelationRawValue,
  dataSource: ComputeRollupValueOptions['view']['manager']['dataSource']
):
  | {
      status: 'ok';
      targetMeta: PropertyMetaConfig;
      targetData: unknown;
      entries: RollupEntry[];
    }
  | { status: 'missing' | 'cycle' } => {
  const targetColumn = targetDb.props.columns$.value.find(
    c => c.id === targetPropertyId
  );
  if (!targetColumn) return { status: 'missing' };
  if (targetColumn.type === ROLLUP_PROPERTY_TYPE) return { status: 'cycle' };

  const targetMeta = dataSource.propertyMetaGet(targetColumn.type);
  if (!targetMeta) return { status: 'missing' };

  const formulaView =
    targetColumn.type === FORMULA_PROPERTY_TYPE
      ? buildFormulaView(targetDb, dataSource)
      : null;

  const entries = refs.map(ref => {
    if (targetColumn.type === FORMULA_PROPERTY_TYPE) {
      const jsonValue = computeFormulaValue(
        formulaView as never,
        ref.blockId,
        targetColumn.id,
        targetColumn.data as FormulaPropertyData
      );
      return { rawValue: jsonValue, jsonValue };
    }

    const rawValue = getRawCellValue(
      targetDb,
      ref.blockId,
      targetColumn.id,
      targetMeta
    );
    const jsonValue = targetMeta.config.rawValue.toJson({
      value: rawValue as never,
      data: targetColumn.data as never,
      dataSource: dataSource as never,
    });
    return { rawValue, jsonValue };
  });

  return {
    status: 'ok',
    targetMeta,
    targetData: targetColumn.data,
    entries,
  };
};

export const aggregateRollupEntries = (params: {
  aggregationType: string;
  entries: RollupEntry[];
  targetMeta: PropertyMetaConfig;
  targetData: unknown;
  dataSource: ComputeRollupValueOptions['view']['manager']['dataSource'];
}): string => {
  const { aggregationType, entries, targetMeta, targetData, dataSource } =
    params;

  if (aggregationType === 'show-original') {
    return entries
      .map(entry => rawValueToString(targetMeta, entry.rawValue, targetData))
      .filter(Boolean)
      .join(', ');
  }

  if (aggregationType === 'show-unique') {
    return Array.from(
      new Set(
        entries
          .map(entry =>
            rawValueToString(targetMeta, entry.rawValue, targetData)
          )
          .filter(Boolean)
      )
    ).join(', ');
  }

  const fn = statsFunctions.find(f => f.type === aggregationType);
  if (!fn?.impl) return entries.length.toString();

  try {
    return fn.impl(entries.map(entry => entry.jsonValue) as never, {
      meta: targetMeta,
      dataSource: dataSource as never,
    });
  } catch (err) {
    console.error('Rollup aggregation failed', err);
    return '';
  }
};

export const computeRollupValue = ({
  view,
  rowId,
  data,
  std,
  onLoadNeeded,
}: ComputeRollupValueOptions): RollupComputation => {
  if (
    !data.sourceRelationPropertyId ||
    !data.targetPropertyId ||
    !data.aggregationType
  ) {
    return empty('not-configured');
  }

  const sourceRelationCell = view.cellGetOrCreate(
    rowId,
    data.sourceRelationPropertyId
  );
  const refs =
    ((sourceRelationCell.value$?.value ??
      sourceRelationCell.jsonValue$?.value) as RelationRawValue | null) ?? [];
  if (refs.length === 0) return empty('empty');

  const sourceRelationProperty = view.propertyGetOrCreate(
    data.sourceRelationPropertyId
  );
  const relationData = sourceRelationProperty.data$
    .value as RelationPropertyData;
  if (!relationData.targetBlockId || !std) return empty('missing');

  const targetDocId = relationData.targetDocId ?? std.store.id;
  const targetDoc = std.workspace.getDoc(targetDocId);
  if (!targetDoc) return empty('missing');
  if (!targetDoc.loaded) {
    onLoadNeeded?.(targetDocId);
    return empty('loading');
  }

  const targetDb = targetDoc
    .getStore({ id: targetDoc.id })
    .getBlock(relationData.targetBlockId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!targetDb) return empty('missing');

  const collected = collectTargetEntries(
    targetDb,
    data.targetPropertyId,
    refs,
    view.manager.dataSource
  );
  if (collected.status !== 'ok') return empty(collected.status);

  return {
    status: 'ok',
    value: aggregateRollupEntries({
      aggregationType: data.aggregationType,
      entries: collected.entries,
      targetMeta: collected.targetMeta,
      targetData: collected.targetData,
      dataSource: view.manager.dataSource,
    }),
  };
};
